"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function TypingText({
  text = ""
}: {
  text?: string;
}) {
  const [displayedText, setDisplayedText] = useState("");
  useEffect(() => {
    if (!text) return;
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(interval);
    }, 60);
    return () => clearInterval(interval);
  }, [text]);
  return <span className="inline-flex items-center">
      {displayedText}
      <span className="ml-1 w-0.5 h-[1.2em] bg-primary animate-pulse shrink-0" aria-hidden="true" />
    </span>;
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-24 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors bg-transparent hover:bg-transparent focus:bg-transparent">
                <TypingText text={item.label} />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      <nav className="flex flex-col gap-2">
        {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
            <Link to={item.href || "#"} className="flex items-center py-3 text-lg font-medium text-foreground border-b border-border/40 hover:text-primary transition-colors">
              <TypingText text={item.label} />
            </Link>
          </SheetClose>)}
      </nav>
      <div className="flex flex-col gap-4 mt-4">
        <SheetClose asChild>
          <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base">
            <Link to="#">Demander un devis</Link>
          </Button>
        </SheetClose>
      </div>
    </div>;
}
export default function NavigationHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="en-tête-de-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 transition-opacity hover:opacity-90">
          <img src="https://nsdb.10web.cloud/wp-content/uploads/2026/03/Logo-Audit-26.08.25.jpg" alt="Nouvelle Société Du Bâtiment" className="w-auto object-contain !h-[70px]" />
        </Link>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="3" className="flex">
            <DesktopMenu />
          </MenuProvider>
          
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-sm">
            <Link to="#">Demander un devis</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground hover:bg-accent">
                <Menu className="size-6" />
                <span className="sr-only">Ouvrir le menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l border-border bg-background p-6">
              <SheetHeader className="text-left mb-4">
                <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
                <Link to="/" className="inline-block">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564310/img/nsb_logo_forest_green.png" alt="Nouvelle Société Du Bâtiment" className="h-8 w-auto object-contain" />
                </Link>
              </SheetHeader>
              
              <MenuProvider menu_id="3" className="flex flex-col h-full">
                <MobileMenu />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}